'use strict';

/**
 * The Prompt Library page (/prompts), three tabs:
 *
 *   My Library      The pict-section-prompteditor section, REST-backed through
 *                   PlansheetPromptProvider, fed the merged shape cascade
 *                   (platform defaults shadowed by org adoptions, plus the
 *                   caller's own shapes). A toolbar above it drives the
 *                   refinement cycle for the selected prompt: record a run,
 *                   post to the shared library, view history, and paste in a
 *                   freeform prompt that was built and run outside plansheet.
 *
 *   Shared Library  What teammates have posted (posting is gated on at least
 *                   one recorded run). Read a prompt, its versions and runs,
 *                   discuss it (comments attach via OwnerType 'Prompt'),
 *                   record a run of it, or fork it into your own library.
 *
 *   My Shapes       The user end of the shape cascade (Plansheet-PromptShapes
 *                   in User scope): fork org shapes or author personal ones.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libMarkdownRender = require('./MarkdownRender.js');

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _shortDate(pDate)
{
	if (!pDate) { return ''; }
	let tmpStamp = Date.parse(pDate);
	if (isNaN(tmpStamp)) { return String(pDate); }
	return new Date(tmpStamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const _TABS =
[
	{ Key: 'library', Label: 'My Library' },
	{ Key: 'shared', Label: 'Shared Library' },
	{ Key: 'shapes', Label: 'My Shapes' }
];

const _ViewConfiguration =
{
	ViewIdentifier: 'Plansheet-PromptLibrary',
	DefaultRenderable: 'Plansheet-PromptLibrary',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,

	CSS: /*css*/`
		.ppl { display: flex; flex-direction: column; height: 100%; padding: 12px 16px; box-sizing: border-box; }
		.ppl-tabs { flex-shrink: 0; display: flex; gap: 4px; border-bottom: 1px solid var(--theme-color-border-light, #e3e3e3); margin-bottom: 10px; }
		.ppl-tab { padding: 7px 14px; border: none; background: transparent; cursor: pointer; font-size: 14px; color: var(--theme-color-text-secondary, #667); border-bottom: 2px solid transparent; }
		.ppl-tab:hover { color: var(--theme-color-text-primary, #222); }
		.ppl-tab-active { color: var(--theme-color-brand-primary, #2e7d74); border-bottom-color: var(--theme-color-brand-primary, #2e7d74); font-weight: 600; }
		.ppl-content { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: auto; }
		.ppl-pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
		.ppl-toolbar { flex-shrink: 0; display: flex; gap: 8px; align-items: center; padding: 0 0 10px 0; flex-wrap: wrap; }
		.ppl-toolbar-note { font-size: 12px; color: var(--theme-color-text-secondary, #889); }
		.ppl-btn { border: 1px solid var(--theme-color-border-light, #ccc); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #223); border-radius: 6px; padding: 5px 12px; font-size: 12.5px; cursor: pointer; white-space: nowrap; }
		.ppl-btn:hover { border-color: var(--theme-color-brand-primary, #2e7d74); color: var(--theme-color-brand-primary, #2e7d74); }
		.ppl-btn-primary { background: var(--theme-color-brand-primary, #2e7d74); border-color: var(--theme-color-brand-primary, #2e7d74); color: var(--theme-color-text-on-brand, #fff); }
		.ppl-btn-primary:hover { color: var(--theme-color-text-on-brand, #fff); opacity: 0.92; }
		.ppl-editor-host { flex: 1; min-height: 0; }
		.ppl-shared { display: flex; gap: 16px; min-height: 0; flex: 1; }
		.ppl-shared-list { width: 330px; flex-shrink: 0; overflow: auto; display: flex; flex-direction: column; gap: 8px; }
		.ppl-card { border: 1px solid var(--theme-color-border-light, #e3e3e3); border-radius: 8px; padding: 10px 12px; cursor: pointer; background: var(--theme-color-background-primary, transparent); text-align: left; color: var(--theme-color-text-primary, #223); font-family: inherit; }
		.ppl-card:hover { border-color: var(--theme-color-brand-primary, #2e7d74); }
		.ppl-card-active { border-color: var(--theme-color-brand-primary, #2e7d74); box-shadow: 0 0 0 1px var(--theme-color-brand-primary, #2e7d74) inset; }
		.ppl-card-title { font-weight: 600; font-size: 14px; margin-bottom: 3px; color: var(--theme-color-text-primary, #1d2230); }
		.ppl-card-meta { font-size: 11.5px; color: var(--theme-color-text-secondary, #778); display: flex; gap: 8px; flex-wrap: wrap; }
		.ppl-badge { font-size: 10.5px; padding: 1px 7px; border-radius: 8px; border: 1px solid var(--theme-color-border-light, #ccc); color: var(--theme-color-text-secondary, #667); }
		.ppl-shared-detail { flex: 1; min-width: 0; overflow: auto; border: 1px solid var(--theme-color-border-light, #e3e3e3); border-radius: 8px; padding: 14px 16px; }
		.ppl-detail-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
		.ppl-detail-head h3 { margin: 0; font-size: 17px; }
		.ppl-detail-actions { display: flex; gap: 8px; margin: 10px 0 14px 0; flex-wrap: wrap; }
		.ppl-source { border: 1px solid var(--theme-color-border-light, #eee); border-radius: 7px; padding: 12px 14px; margin-bottom: 14px; }
		.ppl-subhead { font-size: 13px; font-weight: 600; color: var(--theme-color-text-secondary, #667); margin: 14px 0 6px 0; }
		.ppl-history-row { font-size: 12.5px; padding: 5px 2px; border-bottom: 1px solid var(--theme-color-border-light, #eee); display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
		.ppl-history-row:last-child { border-bottom: none; }
		.ppl-empty { color: var(--theme-color-text-secondary, #889); font-size: 13px; padding: 14px 6px; }
		.ppl-comments { margin-top: 16px; }
	`,

	Templates:
	[
		{
			Hash: 'Plansheet-PromptLibrary',
			Template: /*html*/`
<div class="ppl">
	<h2 class="ps-page-h">Prompt Library</h2>
	<div class="ppl-tabs">{~TS:Plansheet-PromptLibrary-Tab:AppData.PromptLibrary.Tabs~}</div>
	<div class="ppl-content">
		{~TS:Plansheet-PromptLibrary-Pane-Library:AppData.PromptLibrary.LibrarySlot~}
		{~TS:Plansheet-PromptLibrary-Pane-Shared:AppData.PromptLibrary.SharedSlot~}
		{~TS:Plansheet-PromptLibrary-Pane-Shapes:AppData.PromptLibrary.ShapesSlot~}
	</div>
</div>`
		},
		{ Hash: 'Plansheet-PromptLibrary-Tab', Template: /*html*/`<button class="ppl-tab {~D:Record.ActiveClass~}" onclick="_Pict.views['Plansheet-PromptLibrary'].showTab('{~D:Record.Key~}')">{~D:Record.Label~}</button>` },
		{
			Hash: 'Plansheet-PromptLibrary-Pane-Library',
			Template: /*html*/`
<div class="ppl-pane">
	<div class="ppl-toolbar">
		<button class="ppl-btn" onclick="_Pict.views['Plansheet-PromptLibrary'].recordRunForActive()">Record run</button>
		<button class="ppl-btn" onclick="_Pict.views['Plansheet-PromptLibrary'].postActive()">Post to shared library</button>
		<button class="ppl-btn" onclick="_Pict.views['Plansheet-PromptLibrary'].historyForActive()">History</button>
		<button class="ppl-btn ppl-btn-primary" onclick="_Pict.views['Plansheet-PromptLibrary'].pasteExternal()">Paste a prompt I ran elsewhere</button>
		<span class="ppl-toolbar-note">Runs, posts, and attachments pin the version they happened against.</span>
	</div>
	<div class="ppl-editor-host" id="PromptLibrary-Editor-Container"></div>
</div>`
		},
		{
			Hash: 'Plansheet-PromptLibrary-Pane-Shared',
			Template: /*html*/`
<div class="ppl-pane ppl-shared">
	<div class="ppl-shared-list" id="PromptLibrary-Shared-List"></div>
	<div class="ppl-shared-detail" id="PromptLibrary-Shared-Detail"></div>
</div>`
		},
		{ Hash: 'Plansheet-PromptLibrary-Pane-Shapes', Template: /*html*/`<div class="ppl-pane" id="PromptLibrary-Shapes-Container"></div>` }
	],

	Renderables:
	[
		{ RenderableHash: 'Plansheet-PromptLibrary', TemplateHash: 'Plansheet-PromptLibrary', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

class PictViewPromptLibrary extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// The tab a route asked for, consumed by show() once its data is in.
		this._pendingTab = '';
		if (!this.pict.AppData.PromptLibrary)
		{
			this.pict.AppData.PromptLibrary =
			{
				ActiveTab: 'library',
				Tabs: [],
				LibrarySlot: [],
				SharedSlot: [],
				ShapesSlot: [],
				Shapes: [],
				Shared: [],
				SelectedShared: null,
				SelectedVersions: [],
				SelectedRuns: []
			};
		}
	}

	_state() { return this.pict.AppData.PromptLibrary; }
	_api() { return this.pict.providers.PromptAPI; }
	_shapeAPI() { return this.pict.providers.PromptShapeAPI; }
	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_editor() { return this.pict.views['Plansheet-PromptEditor']; }
	_me() { return (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {}; }

	/** Enter the page: refresh the shape cascade, then land on the last tab. */
	show()
	{
		// The /prompts route is the clean library (no shared prompt open); a specific shared prompt is its
		// own route (/prompt/:id -> showSharedPrompt), so clear any prior selection here.
		this._state().SelectedShared = null;
		// Authors resolve from the roster (shared cards, run history).
		if (this.pict.providers.TeamAPI && typeof this.pict.providers.TeamAPI.roster === 'function')
		{
			this.pict.providers.TeamAPI.roster().catch(() => { });
		}
		this._shapeAPI().available()
			.then((pShapes) =>
			{
				let tmpState = this._state();
				tmpState.Shapes = pShapes || [];
				this._applyShapesToEditor();
				// The _apply* form: show() is what the route handler already called, so navigating here
				// would send the router round again.
				let tmpWanted = this._pendingTab || tmpState.ActiveTab || 'library';
				this._pendingTab = '';
				this._applyShowTab(tmpWanted);
			})
			.catch(() => { let tmpWanted = this._pendingTab || 'library'; this._pendingTab = ''; this._applyShowTab(tmpWanted); });
	}

	_applyShapesToEditor()
	{
		let tmpState = this._state();
		let tmpDefinitions = (tmpState.Shapes || []).map((pShape) => pShape.Definition).filter(Boolean);
		let tmpEditor = this._editor();
		if (tmpEditor)
		{
			if (tmpDefinitions.length)
			{
				if (typeof tmpEditor.setPromptTypes === 'function') { tmpEditor.setPromptTypes(tmpDefinitions); }
				else { tmpEditor.options.PromptTypes = tmpDefinitions; }
			}
			let tmpMe = this._me();
			tmpEditor.options.CurrentUser = { Key: tmpMe.IDUser || 0, Name: tmpMe.DisplayName || tmpMe.Email || 'Me' };
			if (tmpEditor.options.DataProvider && typeof tmpEditor.options.DataProvider.setPromptTypes === 'function')
			{
				tmpEditor.options.DataProvider.setPromptTypes(tmpDefinitions);
			}
		}
	}

	/**
	 * Switch tabs. NAVIGATES; the route handler renders.
	 *
	 * The three tabs are three different pages: your own library and its editor, the shared library
	 * with its own list and detail, and your shapes. Only /prompt/:id (a shared prompt) could be
	 * linked; which TAB you were on could not, so a reload always dropped you back on the library.
	 */
	showTab(pTab)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showPromptsTab === 'function' && tmpApp.showPromptsTab(pTab)) { return; }
		return this._applyShowTab(pTab);
	}

	// Enter on a named tab from the /prompts/:tab route.
	showTabRoute(pTab)
	{
		this._pendingTab = _TABS.some((pTabDef) => pTabDef.Key === pTab) ? pTab : 'library';
		return this.show();
	}

	_applyShowTab(pTab)
	{
		let tmpState = this._state();
		tmpState.ActiveTab = pTab;
		tmpState.Tabs = _TABS.map((pTabDef) => ({ Key: pTabDef.Key, Label: pTabDef.Label, ActiveClass: (pTabDef.Key === pTab) ? 'ppl-tab-active' : '' }));
		tmpState.LibrarySlot = (pTab === 'library') ? [{}] : [];
		tmpState.SharedSlot = (pTab === 'shared') ? [{}] : [];
		tmpState.ShapesSlot = (pTab === 'shapes') ? [{}] : [];
		this.render();
		this._renderActive(pTab);
	}

	_renderActive(pTab)
	{
		if (pTab === 'library')
		{
			let tmpEditor = this._editor();
			if (!tmpEditor) { return; }
			// Re-pull the shape cascade on every entry to the library so a shape just authored in the
			// Shapes tab shows up in the type dropdown without a full page reload, then load the editor.
			this._shapeAPI().available()
				.then((pShapes) =>
				{
					this._state().Shapes = pShapes || this._state().Shapes || [];
					this._applyShapesToEditor();
					tmpEditor.load();
				})
				.catch(() => tmpEditor.load());
		}
		else if (pTab === 'shared')
		{
			this._loadShared();
		}
		else if (pTab === 'shapes')
		{
			let tmpShapesView = this.pict.views['Plansheet-PromptShapes'];
			if (tmpShapesView) { tmpShapesView.showIn('#PromptLibrary-Shapes-Container', 'User'); }
		}
	}

	// ---- the active prompt in the editor (the refinement cycle drivers) -------

	_activePrompt()
	{
		let tmpEditor = this._editor();
		let tmpKey = tmpEditor && tmpEditor._ui && tmpEditor._ui.ActivePromptKey;
		if (!tmpKey) { return null; }
		let tmpPrompt = (tmpEditor._loaded && tmpEditor._loaded.Prompts || []).find((pCandidate) => pCandidate.Key === tmpKey) || null;
		if (!tmpPrompt) { return null; }
		let tmpServer = (tmpPrompt.Meta && tmpPrompt.Meta._Plansheet) || {};
		if (!tmpServer.IDPrompt) { return null; }
		return { Prompt: tmpPrompt, IDPrompt: tmpServer.IDPrompt, Server: tmpServer };
	}

	_requireActive()
	{
		let tmpActive = this._activePrompt();
		if (!tmpActive) { this._toast('Select a prompt in the editor first.', 'info'); }
		return tmpActive;
	}

	recordRunForActive()
	{
		let tmpActive = this._requireActive();
		if (!tmpActive) { return; }
		this._recordRunDialog(tmpActive.IDPrompt, tmpActive.Prompt.Title);
	}

	_recordRunDialog(pIDPrompt, pTitle, fAfter)
	{
		this._modal().show(
		{
			title: 'Record a run of "' + pTitle + '"',
			content:
				'<p style="font-size:13px;">Where did it run, and how did it go? This pins the current version into history.</p>'
				+ '<label style="font-size:12px;">Outcome</label>'
				+ '<select id="PPL-Run-Outcome" class="pict-input" style="width:100%; margin-bottom:8px;">'
				+ '<option value="">Not rated</option><option value="Success">Success</option>'
				+ '<option value="Mixed">Mixed</option><option value="Failure">Failure</option></select>'
				+ '<label style="font-size:12px;">Notes (what happened, what to change next time)</label>'
				+ '<textarea id="PPL-Run-Notes" class="pict-input" style="width:100%; min-height:90px;"></textarea>'
				+ '<label style="font-size:12px; margin-top:6px; display:inline-flex; gap:6px; align-items:center;">'
				+ '<input type="checkbox" id="PPL-Run-External"> It ran outside plansheet</label>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Record run', Style: 'primary' }
			]
		}).then((pChoice) =>
		{
			if (pChoice !== 'save') { return; }
			let tmpOutcome = (document.getElementById('PPL-Run-Outcome') || {}).value || '';
			let tmpNotes = (document.getElementById('PPL-Run-Notes') || {}).value || '';
			let tmpExternal = !!((document.getElementById('PPL-Run-External') || {}).checked);
			this._api().recordRun(pIDPrompt, { Outcome: tmpOutcome, Notes: tmpNotes, RunContext: tmpExternal ? 'External' : 'Manual' })
				.then(() =>
				{
					this._toast('Run recorded; version pinned.', 'success');
					if (typeof fAfter === 'function') { fAfter(); }
				})
				.catch((pError) => this._toast(pError.message, 'error'));
		});
	}

	postActive()
	{
		let tmpActive = this._requireActive();
		if (!tmpActive) { return; }
		if (tmpActive.Server.Visibility === 'Shared')
		{
			return this._toast('Already in the shared library.', 'info');
		}
		this._modal().confirm('Posting shares this prompt (and its pinned version history) with everyone in the organization. It needs at least one recorded run first.',
			{ title: 'Post "' + tmpActive.Prompt.Title + '" to the shared library?', confirmLabel: 'Post' })
			.then((pOk) =>
			{
				if (!pOk) { return; }
				this._api().post(tmpActive.IDPrompt)
					.then(() =>
					{
						this._toast('Posted to the shared library.', 'success');
						let tmpEditor = this._editor();
						if (tmpEditor) { tmpEditor.refresh(); }
					})
					.catch((pError) => this._toast(pError.message, 'error'));
			});
	}

	historyForActive()
	{
		let tmpActive = this._requireActive();
		if (!tmpActive) { return; }
		this.showHistory(tmpActive.IDPrompt, tmpActive.Prompt.Title);
	}

	showHistory(pIDPrompt, pTitle)
	{
		Promise.all([this._api().versions(pIDPrompt), this._api().runs(pIDPrompt)])
			.then((pResults) =>
			{
				let tmpVersions = pResults[0].Versions || [];
				let tmpRuns = pResults[1].Runs || [];
				let tmpHTML = '<div style="max-height:55vh; overflow:auto;">';
				tmpHTML += '<div style="font-weight:600; font-size:13px; margin-bottom:5px;">Versions</div>';
				tmpHTML += tmpVersions.length ? '' : '<p style="font-size:12.5px;">None captured yet; versions appear as the prompt is edited.</p>';
				tmpVersions.forEach((pVersion) =>
				{
					tmpHTML += '<div style="border-bottom:1px solid #ddd; padding:6px 0; font-size:12.5px;">'
						+ '<strong>v' + pVersion.Sequence + '</strong>'
						+ (pVersion.Pinned ? ' <span style="font-size:11px; opacity:0.8;">(pinned)</span>' : '')
						+ ' <span style="opacity:0.75;">' + _escapeHTML(pVersion.Note || '') + ' - ' + _escapeHTML(_shortDate(pVersion.UpdateDate || pVersion.CreateDate)) + '</span>'
						+ ' <button class="ppl-btn" style="float:right;" onclick="_Pict.views[\'Plansheet-PromptLibrary\'].showVersionSource(' + pVersion.IDPromptVersion + ', ' + pIDPrompt + ')">View</button>'
						+ '</div>';
				});
				tmpHTML += '<div style="font-weight:600; font-size:13px; margin:12px 0 5px 0;">Runs</div>';
				tmpHTML += tmpRuns.length ? '' : '<p style="font-size:12.5px;">No runs recorded yet.</p>';
				tmpRuns.forEach((pRun) =>
				{
					tmpHTML += '<div style="border-bottom:1px solid #ddd; padding:6px 0; font-size:12.5px;">'
						+ '<strong>' + _escapeHTML(pRun.RunContext || 'Manual') + '</strong>'
						+ (pRun.Outcome ? ' <span class="ppl-badge">' + _escapeHTML(pRun.Outcome) + '</span>' : '')
						+ ' <span style="opacity:0.75;">' + _escapeHTML(_shortDate(pRun.CreateDate)) + '</span>'
						+ (pRun.Notes ? '<div style="margin-top:2px;">' + _escapeHTML(pRun.Notes) + '</div>' : '')
						+ '</div>';
				});
				tmpHTML += '</div>';
				this._modal().show(
				{
					title: 'History: ' + pTitle,
					content: tmpHTML,
					buttons: [{ Hash: 'ok', Label: 'Close', Style: 'primary' }]
				});
			})
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	showVersionSource(pIDPromptVersion, pIDPrompt)
	{
		this._api().versions(pIDPrompt).then((pBody) =>
		{
			let tmpVersion = (pBody.Versions || []).find((pCandidate) => Number(pCandidate.IDPromptVersion) === Number(pIDPromptVersion));
			if (!tmpVersion) { return; }
			this._modal().show(
			{
				title: 'v' + tmpVersion.Sequence + (tmpVersion.Note ? (' - ' + tmpVersion.Note) : ''),
				content: '<pre style="max-height:55vh; overflow:auto; white-space:pre-wrap; font-size:12px;">' + _escapeHTML(tmpVersion.Source || '') + '</pre>',
				buttons: [{ Hash: 'ok', Label: 'Close', Style: 'primary' }]
			});
		});
	}

	pasteExternal()
	{
		this._modal().show(
		{
			title: 'Paste a prompt you built and ran elsewhere',
			content:
				'<p style="font-size:13px;">It lands in your library as a freeform prompt with an external run already recorded, so it can be posted, attached, and refined like anything crafted here.</p>'
				+ '<label style="font-size:12px;">Title</label>'
				+ '<input id="PPL-Paste-Title" class="pict-input" style="width:100%; margin-bottom:8px;">'
				+ '<label style="font-size:12px;">The prompt</label>'
				+ '<textarea id="PPL-Paste-Source" class="pict-input" style="width:100%; min-height:150px; font-family:monospace; font-size:12px;"></textarea>'
				+ '<label style="font-size:12px;">How did it go? (optional notes)</label>'
				+ '<textarea id="PPL-Paste-Notes" class="pict-input" style="width:100%; min-height:54px;"></textarea>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Add to my library', Style: 'primary' }
			]
		}).then((pChoice) =>
		{
			if (pChoice !== 'save') { return; }
			let tmpTitle = ((document.getElementById('PPL-Paste-Title') || {}).value || '').trim();
			let tmpSource = (document.getElementById('PPL-Paste-Source') || {}).value || '';
			let tmpNotes = (document.getElementById('PPL-Paste-Notes') || {}).value || '';
			if (!tmpTitle || !tmpSource.trim()) { return this._toast('A title and the prompt text are both needed.', 'error'); }
			this._api().paste({ Title: tmpTitle, Source: tmpSource, Notes: tmpNotes })
				.then(() =>
				{
					this._toast('Added to your library with its external run.', 'success');
					let tmpEditor = this._editor();
					if (tmpEditor && this._state().ActiveTab === 'library') { tmpEditor.refresh(); }
				})
				.catch((pError) => this._toast(pError.message, 'error'));
		});
	}

	// ---- the shared library -----------------------------------------------------

	_loadShared()
	{
		let tmpState = this._state();
		this._api().shared()
			.then((pPrompts) =>
			{
				tmpState.Shared = pPrompts || [];
				if (tmpState.SelectedShared && !tmpState.Shared.find((pRow) => pRow.IDPrompt === tmpState.SelectedShared))
				{
					tmpState.SelectedShared = null;
				}
				this._paintShared();
				// Restore the open prompt's detail in place (NOT openShared -- that navigates and would loop).
				if (tmpState.SelectedShared) { this._openSharedRender(tmpState.SelectedShared); }
			})
			.catch(() => this._paintShared());
	}

	// pStampedName is the server-resolved AuthorDisplayName riding the row; it
	// covers departed teammates the roster no longer lists.
	_authorName(pIDUser, pStampedName)
	{
		let tmpMe = this._me();
		if (String(tmpMe.IDUser) === String(pIDUser)) { return 'you'; }
		if (pStampedName) { return pStampedName; }
		let tmpTeam = this.pict.AppData.Team || {};
		let tmpPeople = [].concat(tmpTeam.Roster || [], tmpTeam.Members || []);
		let tmpHit = tmpPeople.find((pPerson) => String(pPerson.IDUser) === String(pIDUser));
		return tmpHit ? (tmpHit.DisplayName || tmpHit.Email || ('User ' + pIDUser)) : ('User ' + pIDUser);
	}

	_shapeName(pTypeKey)
	{
		let tmpShape = (this._state().Shapes || []).find((pCandidate) => pCandidate.Key === pTypeKey);
		return tmpShape ? tmpShape.Name : pTypeKey;
	}

	_paintShared()
	{
		let tmpState = this._state();
		let tmpHost = document.getElementById('PromptLibrary-Shared-List');
		if (!tmpHost) { return; }
		let tmpHTML = '';
		tmpState.Shared.forEach((pPrompt) =>
		{
			let tmpActive = (tmpState.SelectedShared === pPrompt.IDPrompt) ? ' ppl-card-active' : '';
			tmpHTML += '<button class="ppl-card' + tmpActive + '" onclick="_Pict.views[\'Plansheet-PromptLibrary\'].openShared(' + pPrompt.IDPrompt + ')">'
				+ '<div class="ppl-card-title">' + _escapeHTML(pPrompt.Title) + '</div>'
				+ '<div class="ppl-card-meta">'
				+ '<span>#' + (pPrompt.PromptNumber || pPrompt.IDPrompt) + '</span>'
				+ '<span>' + _escapeHTML(this._shapeName(pPrompt.TypeKey)) + '</span>'
				+ '<span>by ' + _escapeHTML(this._authorName(pPrompt.IDUser, pPrompt.AuthorDisplayName)) + '</span>'
				+ '<span>' + _escapeHTML(_shortDate(pPrompt.PostedDate)) + '</span>'
				+ (pPrompt.Origin === 'External' ? '<span class="ppl-badge">ran elsewhere</span>' : '')
				+ '</div>'
				+ '</button>';
		});
		tmpHost.innerHTML = tmpHTML || '<div class="ppl-empty">Nothing posted yet. Post a prompt from My Library once it has a recorded run.</div>';

		if (!tmpState.SelectedShared)
		{
			let tmpDetail = document.getElementById('PromptLibrary-Shared-Detail');
			if (tmpDetail) { tmpDetail.innerHTML = '<div class="ppl-empty">Pick a prompt to read it, see its versions and runs, discuss it, or fork it into your library.</div>'; }
		}
	}

	// Opening a shared prompt is a NAVIGATION to /prompt/:id, so it deep-links and survives a reload (the
	// open prompt used to live only in memory). The route calls showSharedPrompt, which lands on the shared
	// tab; _openSharedRender does the actual detail render.
	openShared(pIDPrompt)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showPrompt === 'function') { tmpApp.showPrompt(pIDPrompt); }
		else { this._openSharedRender(pIDPrompt); }
	}

	// The detail render for a shared prompt: select it, paint the list highlight + the versions/runs pane.
	// Called by the route (via showSharedPrompt) and internally by _loadShared to restore the open prompt
	// after the shared list reloads (which is why this must NOT navigate -- it would loop the route).
	_openSharedRender(pIDPrompt)
	{
		let tmpState = this._state();
		tmpState.SelectedShared = pIDPrompt;
		this._paintShared();
		Promise.all([this._api().versions(pIDPrompt), this._api().runs(pIDPrompt)])
			.then((pResults) =>
			{
				let tmpPrompt = pResults[0].Prompt || null;
				tmpState.SelectedVersions = pResults[0].Versions || [];
				tmpState.SelectedRuns = pResults[1].Runs || [];
				this._paintSharedDetail(tmpPrompt);
			})
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	// Route entry for /prompt/:id: land on the shared tab with prompt :id open. Loads the shape cascade
	// (shape names in the detail) first; showTab('shared') -> _loadShared then restores the detail for the
	// SelectedShared we set here.
	showSharedPrompt(pIDPrompt)
	{
		let tmpID = Number(pIDPrompt) || 0;
		this._state().SelectedShared = tmpID;
		let tmpSelf = this;
		if (this.pict.providers.TeamAPI && typeof this.pict.providers.TeamAPI.roster === 'function') { this.pict.providers.TeamAPI.roster().catch(() => { }); }
		let fReady = () => { tmpSelf.showTab('shared'); };
		this._shapeAPI().available()
			.then((pShapes) => { tmpSelf._state().Shapes = pShapes || tmpSelf._state().Shapes || []; tmpSelf._applyShapesToEditor(); fReady(); })
			.catch(fReady);
	}

	_paintSharedDetail(pPrompt)
	{
		let tmpState = this._state();
		let tmpHost = document.getElementById('PromptLibrary-Shared-Detail');
		if (!tmpHost || !pPrompt) { return; }
		let tmpViewRef = "_Pict.views['Plansheet-PromptLibrary']";

		let tmpHTML = '<div class="ppl-detail-head">'
			+ '<h3>' + _escapeHTML(pPrompt.Title) + '</h3>'
			+ '<span class="ppl-badge">#' + (pPrompt.PromptNumber || pPrompt.IDPrompt) + '</span>'
			+ '<span class="ppl-badge">' + _escapeHTML(this._shapeName(pPrompt.TypeKey)) + '</span>'
			+ (pPrompt.Origin === 'External' ? '<span class="ppl-badge">ran elsewhere</span>' : '')
			+ '<span class="ppl-card-meta">by ' + _escapeHTML(this._authorName(pPrompt.IDUser, pPrompt.AuthorDisplayName)) + ', posted ' + _escapeHTML(_shortDate(pPrompt.PostedDate)) + '</span>'
			+ '</div>'
			+ '<div class="ppl-detail-actions">'
			+ '<button class="ppl-btn ppl-btn-primary" onclick="' + tmpViewRef + '.forkShared(' + pPrompt.IDPrompt + ')">Fork to my library</button>'
			+ '<button class="ppl-btn" onclick="' + tmpViewRef + '.recordRunForShared(' + pPrompt.IDPrompt + ')">Record a run</button>'
			+ '<button class="ppl-btn" onclick="' + tmpViewRef + '.showHistory(' + pPrompt.IDPrompt + ', \'' + _escapeHTML(pPrompt.Title).replace(/'/g, '') + '\')">Full history</button>'
			+ '</div>'
			+ '<div class="ppl-source" id="PromptLibrary-Shared-Source"></div>';

		tmpHTML += '<div class="ppl-subhead">Versions (' + tmpState.SelectedVersions.length + ')</div>';
		tmpState.SelectedVersions.slice(0, 5).forEach((pVersion) =>
		{
			tmpHTML += '<div class="ppl-history-row">'
				+ '<strong>v' + pVersion.Sequence + '</strong>'
				+ (pVersion.Pinned ? '<span class="ppl-badge">pinned</span>' : '')
				+ '<span>' + _escapeHTML(pVersion.Note || '') + '</span>'
				+ '<span style="opacity:0.7;">' + _escapeHTML(_shortDate(pVersion.UpdateDate || pVersion.CreateDate)) + '</span>'
				+ '</div>';
		});

		tmpHTML += '<div class="ppl-subhead">Runs (' + tmpState.SelectedRuns.length + ')</div>';
		tmpState.SelectedRuns.slice(0, 5).forEach((pRun) =>
		{
			tmpHTML += '<div class="ppl-history-row">'
				+ '<strong>' + _escapeHTML(pRun.RunContext || 'Manual') + '</strong>'
				+ (pRun.Outcome ? '<span class="ppl-badge">' + _escapeHTML(pRun.Outcome) + '</span>' : '')
				+ '<span>by ' + _escapeHTML(this._authorName(pRun.CreatingIDUser, pRun.AuthorDisplayName)) + '</span>'
				+ '<span style="opacity:0.7;">' + _escapeHTML(_shortDate(pRun.CreateDate)) + '</span>'
				+ (pRun.Notes ? '<span>' + _escapeHTML(pRun.Notes) + '</span>' : '')
				+ '</div>';
		});
		if (!tmpState.SelectedRuns.length) { tmpHTML += '<div class="ppl-empty">No runs recorded.</div>'; }

		tmpHTML += '<div class="ppl-subhead">Discussion</div><div class="ppl-comments" id="Entity-Comments"></div>';

		tmpHost.innerHTML = tmpHTML;

		// The latest version's Source is the document of record for shared
		// reading (a private working copy may have moved past it).
		let tmpSource = (tmpState.SelectedVersions[0] && tmpState.SelectedVersions[0].Source) || pPrompt.Source || '';
		let tmpSourceHost = document.getElementById('PromptLibrary-Shared-Source');
		if (tmpSourceHost)
		{
			libMarkdownRender.renderInto(this.pict.providers.Content, tmpSourceHost, tmpSource);
		}

		// Discussion threads attach to the prompt itself (OwnerType 'Prompt').
		this._mountComments(pPrompt.IDPrompt);
	}

	_mountComments(pIDPrompt)
	{
		let tmpComments = this.pict.views['Plansheet-Comments'];
		if (!tmpComments) { return; }
		let tmpMe = this._me();
		tmpComments.options.CurrentUser = { Key: tmpMe.IDUser || 0, Name: tmpMe.DisplayName || tmpMe.Email || 'Me', Avatar: tmpMe.AvatarURL || '' };
		tmpComments.options.ReadOnly = false;
		if (this._CommentsLoadedFor === ('Prompt-' + pIDPrompt))
		{
			tmpComments.render();
			return;
		}
		this._CommentsLoadedFor = 'Prompt-' + pIDPrompt;
		tmpComments.setContext({ OwnerType: 'Prompt', IDOwner: pIDPrompt });
	}

	forkShared(pIDPrompt)
	{
		this._api().fork(pIDPrompt)
			.then(() =>
			{
				this._toast('Forked into your library.', 'success');
				this.showTab('library');
			})
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	recordRunForShared(pIDPrompt)
	{
		let tmpPrompt = this._state().Shared.find((pCandidate) => pCandidate.IDPrompt === pIDPrompt);
		this._recordRunDialog(pIDPrompt, tmpPrompt ? tmpPrompt.Title : 'prompt', () => this.openShared(pIDPrompt));
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this._modal();
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewPromptLibrary;
module.exports.default_configuration = _ViewConfiguration;
